/**
 * /posvenda/assistencias — Lista de assistencias ao cliente.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiAlertTriangle,
  FiGrid,
  FiPlus,
  FiRefreshCw,
} from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import { useAuth } from "../../../context/AuthContext";
import {
  formatarHorasRestantes,
  listarAssistencias,
  PRIORIDADES,
  PRIORIDADE_LABELS,
  PRIORIDADE_TONES,
  SITUACAO_LABELS,
  SITUACAO_TONES,
  TIPOS_ASSISTENCIA,
  TIPO_LABELS,
} from "../../../services/posvenda";
import { PageContainer } from "../../../styles/shared";

import { fmtData } from "../../Orcamentos/helpers";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
} from "../../Orcamentos/styles";


export default function AssistenciasLista() {
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtroSituacao, setFiltroSituacao] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroPrioridade, setFiltroPrioridade] = useState("");
  const [apenasAtrasadas, setApenasAtrasadas] = useState(false);

  const podeCriar = isMaster || hasAllPermissions(["assistencia:create"]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await listarAssistencias(request, {
        situacao: filtroSituacao || undefined,
        tipo: filtroTipo || undefined,
        prioridade: filtroPrioridade || undefined,
        atrasadas: apenasAtrasadas || undefined,
      });
      const list = Array.isArray(payload) ? payload : payload?.results || [];
      setItems(list);
    } catch (err) {
      setError(err?.message || "Erro ao carregar assistências.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [request, filtroSituacao, filtroTipo, filtroPrioridade, apenasAtrasadas]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const kpis = useMemo(() => {
    const abertas = items.filter((a) => a.situacao === "ABERTA").length;
    const emAnalise = items.filter((a) => a.situacao === "EM_ANALISE").length;
    const emResolucao = items.filter((a) => a.situacao === "EM_RESOLUCAO").length;
    const atrasadas = items.filter((a) => a.atrasada).length;
    return [
      { label: "Total na lista", value: items.length, tone: "neutral" },
      { label: "Abertas", value: abertas, tone: abertas > 0 ? "warning" : "neutral" },
      { label: "Em análise", value: emAnalise, tone: emAnalise > 0 ? "info" : "neutral" },
      { label: "Em resolução", value: emResolucao, tone: emResolucao > 0 ? "info" : "neutral" },
      {
        label: "Atrasadas (SLA)",
        value: atrasadas,
        tone: atrasadas > 0 ? "danger" : "success",
        sub: atrasadas > 0 ? "clique para filtrar" : "",
        onClick: atrasadas > 0
          ? () => setApenasAtrasadas(!apenasAtrasadas)
          : undefined,
      },
    ];
  }, [items, apenasAtrasadas]);

  return (
    <PageContainer>
      <EntityHeader
        title="Assistência ao cliente"
        subtitle="Garantias, defeitos, reclamações pós-entrega (ADR-0027)"
        actions={[
          {
            label: "Atualizar",
            icon: <FiRefreshCw size={14} aria-hidden="true" />,
            onClick: carregar,
          },
          {
            label: "Ver Kanban",
            icon: <FiGrid size={14} aria-hidden="true" />,
            onClick: () => navigate("/posvenda/assistencias/kanban"),
          },
          ...(podeCriar
            ? [{
                label: "Nova assistência",
                icon: <FiPlus size={14} aria-hidden="true" />,
                onClick: () => navigate("/posvenda/assistencias/criar"),
                primary: true,
              }]
            : []),
        ]}
      />

      <KpiStrip items={kpis} />

      <Card>
        <SectionTitle>Filtros</SectionTitle>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 160 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Situação</span>
            <Select value={filtroSituacao} onChange={(e) => setFiltroSituacao(e.target.value)}>
              <option value="">Todas</option>
              {Object.entries(SITUACAO_LABELS).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </Select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Tipo</span>
            <Select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
              <option value="">Todos</option>
              {TIPOS_ASSISTENCIA.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 140 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Prioridade</span>
            <Select value={filtroPrioridade} onChange={(e) => setFiltroPrioridade(e.target.value)}>
              <option value="">Todas</option>
              {PRIORIDADES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </Select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 22 }}>
            <input
              type="checkbox"
              checked={apenasAtrasadas}
              onChange={(e) => setApenasAtrasadas(e.target.checked)}
            />
            <span style={{ fontSize: 14 }}>Apenas atrasadas</span>
          </label>
        </div>
      </Card>

      {error && <ErrorMsg><FiAlertTriangle size={14} /> {error}</ErrorMsg>}

      <Card>
        <SectionTitle>
          {loading ? "Carregando..." : `${items.length} assistência(s)`}
        </SectionTitle>
        {!loading && items.length === 0 && <EmptyMsg>Nenhuma assistência.</EmptyMsg>}
        {items.length > 0 && (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Tipo</th>
                  <th>Prioridade</th>
                  <th>Título</th>
                  <th>Situação</th>
                  <th>SLA</th>
                  <th>Aberta em</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => navigate(`/posvenda/assistencias/${a.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <td><strong>{a.numero}</strong></td>
                    <td>{a.pedido_numero}</td>
                    <td>{a.cliente_nome}</td>
                    <td>{TIPO_LABELS[a.tipo] || a.tipo}</td>
                    <td>
                      <Pill $tone={PRIORIDADE_TONES[a.prioridade] || "neutral"}>
                        {PRIORIDADE_LABELS[a.prioridade] || a.prioridade}
                      </Pill>
                    </td>
                    <td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={a.titulo}>
                      {a.titulo}
                    </td>
                    <td>
                      <Pill $tone={SITUACAO_TONES[a.situacao] || "neutral"}>
                        {SITUACAO_LABELS[a.situacao] || a.situacao}
                      </Pill>
                      {a.escalonada && (
                        <Pill $tone="warning" style={{ marginLeft: 6 }}>Escalonada</Pill>
                      )}
                    </td>
                    <td>
                      {a.atrasada ? (
                        <Pill $tone="danger">{formatarHorasRestantes(a.horas_restantes)}</Pill>
                      ) : (
                        <span style={{ fontSize: 12 }}>
                          {formatarHorasRestantes(a.horas_restantes)}
                        </span>
                      )}
                    </td>
                    <td>{fmtData(a.aberta_em)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>
    </PageContainer>
  );
}
